import data from "../data/participant-experience.json";
import type { ContractConcept, DocumentRequest, ContractText } from "./participant-contract";
import type { Answers } from "./form-model";
import { resolveParticipantExperience } from "./participant-experience";

export type Experience = {
  id: string; destination: "US" | "ES" | "IT"; title: string;
  concepts: ContractConcept[]; documents: DocumentRequest[];
  pages: Record<string, Array<{title: ContractText; ids: string[]}>>;
  document_groups: Array<{title: ContractText; ids: string[]}>;
  navigation: {profile: string[]; travel: string[]; immigration: string[]; production: string[]};
};
export const experienceCatalog = data;
// DEMO ONLY: remove this switch and demoInputsFromSearch in the production integration.
// Real case context must come from the authorized participant/production records.
export const DEMO_PASSPORT_SWITCHER = false;
// Synthetic contact details for the standalone design preview. Registration replaces these.
export const demoContactAnswers: Answers = {
  "pf.identity.given_names": "Alex",
  "pf.identity.family_primary": "Morgan",
  "pf.contact.email": "alex@example.com",
  "pf.contact.phone": "+12025550123",
};
export const demoInputs: Record<string, unknown> = {
  passport_country: "CO", destination_country: "US", production_kind: "film_television",
  accompanies_principal_o1: false,
  // Demo presets only: short stay, outside Spain. Never infer real case facts from these.
  stay_days: 90, current_country: "ZZ",
  employment_relationship: "subordinate_employment",
};

export function resolveExperience(inputs: Record<string, unknown>) {
  const result = resolveParticipantExperience(data, inputs);
  const experience = result.experience_id ? (data.experiences as unknown as Record<string, Experience>)[result.experience_id] : null;
  return {result, experience};
}

export function suppliedExperienceAnswers(inputs: Record<string, unknown>, experience: Experience): Answers {
  return {
    "pf.engagement.country_of_work": experience.destination,
    "pf.identity.nationalities": String(inputs.passport_country),
    "pf.passport.issuing_country": String(inputs.passport_country),
    ...(experience.destination === "US" ? {"pf.us.mptv_services": inputs.production_kind === "film_television" ? "yes" : "no"} : {}),
    ...(experience.destination === "IT" ? {"pf.engagement.engagement_type": String(inputs.employment_relationship ?? "")} : {}),
    // ZZ is demo-only "outside Spain, exact country unspecified", never a collected country.
    "pf.location.current_country": inputs.current_country && inputs.current_country !== "ZZ" ? String(inputs.current_country) : "",
  };
}

// URL values are demonstration context only, never authenticated CRM input.
export function demoInputsFromSearch(search: string): Record<string, unknown> {
  const query = new URLSearchParams(search);
  const destination = query.get("destination") ?? "US";
  return {
    ...demoInputs, destination_country: destination,
    ...(query.get("requiredOn") ? {first_location_date: query.get("requiredOn")} : {}),
    passport_country: query.get("passport") ?? "CO",
    production_kind: query.get("production") ?? "film_television",
    ...(query.has("employment") ? {employment_relationship: query.get("employment")} : {}),
    ...(query.has("days") && query.get("days") ? {stay_days: Number(query.get("days"))} : {}),
    ...(query.get("current") ? {current_country: query.get("current")} : {}),
    ...Object.fromEntries(["es_eu_rights_exception", "es_unplanned_extension_confirmed", "es_lawful_stay_confirmed", "es_schengen_allowance_confirmed"].filter(key => ["true", "false"].includes(query.get(key) ?? "")).map(key => [key, query.get(key) === "true"])),
    ...(query.get("support") === "true" ? {accompanies_principal_o1: true, principal_o1_id: query.get("principal") ?? ""} : {}),
  };
}
